import React, { useEffect, useRef, useState } from 'react'
import {
  SvgApprove,
  SvgBlueEllipses,
  SvgClone,
  SvgEdit,
  SvgEditHover,
  SvgEllipses,
  SvgReject,
  SvgReview,
  SvgTrash,
  SvgView,
  SvgViewBlue,
} from '../../assets/images'
import useOutsideClick from '../hooks/useOutsideHook'
import ToggleButton from '../ToggleButton'
import {
  ACTION_ICONS_NAMESPACE,
  ActionPopUpPropsType,
  ActionsPopUpListPropsType,
  NAMESPACE,
  OnActionClickCallbackType,
  TABLE_ACTION_TYPE,
  TableActionsPropsTypes,
  TableActionType,
} from '../../utils'
import { isEmpty, noop } from 'lodash'
import Tooltip from '../Tooltip'
import './tableActions.scss'
import ReactDOM from 'react-dom'

export const getIcon = (
  name: string,
  isDisabled: boolean,
  isHovered?: boolean | undefined
) => {
  switch (name) {
    case ACTION_ICONS_NAMESPACE?.EDIT:
      return (
        <span
          className={`custom-cell ${
            isDisabled
              ? 'opacity-20 cursor-not-allowed'
              : 'svg-path-stroke-accent-600'
          }`}
        >
          {isHovered ? <SvgEditHover /> : <SvgEdit />}
        </span>
      )
    case ACTION_ICONS_NAMESPACE?.DELETE:
      return (
        <span
          className={`custom-cell ${
            isDisabled
              ? 'svg-path-stroke-accent-200'
              : 'svg-path-stroke-accent-600'
          }`}
        >
          {isHovered ? (
            <SvgTrash
              height={'0.9rem'}
              width={'0.9rem'}
              className="stroke-secondary-500"
            />
          ) : (
            <SvgTrash height={'0.9rem'} width={'0.9rem'} />
          )}
        </span>
      )
    case ACTION_ICONS_NAMESPACE?.VIEW:
      return (
        <span
          className={`custom-cell ${
            isDisabled
              ? 'svg-path-fill-accent-200'
              : 'svg-path-stroke-accent-600'
          }`}
        >
          {isHovered ? <SvgViewBlue /> : <SvgView />}
        </span>
      )
    case ACTION_ICONS_NAMESPACE?.CLONE:
      return (
        <span
          className={`custom-cell ${
            isDisabled
              ? 'svg-path-fill-accent-200'
              : 'svg-path-fill-secondary-hover svg-path-fill-accent-600'
          }`}
        >
          <SvgClone />
        </span>
      )
    case ACTION_ICONS_NAMESPACE.REJECT:
      return (
        <Tooltip content="Reject">
          <span
            className={`custom-cell ${
              isDisabled
                ? 'svg-path-fill-accent-200'
                : 'svg-path-fill-secondary-hover svg-path-fill-accent-600'
            }`}
          >
            <SvgReject />
          </span>
        </Tooltip>
      )
    case ACTION_ICONS_NAMESPACE.APPROVE:
      return (
        <Tooltip content="Approve">
          <span
            className={`custom-cell ${
              isDisabled
                ? 'svg-path-fill-accent-200'
                : 'svg-path-fill-secondary-hover svg-path-fill-accent-600'
            }`}
          >
            <SvgApprove />
          </span>
        </Tooltip>
      )
    case ACTION_ICONS_NAMESPACE.REVIEW:
      return (
        <Tooltip content="Review">
          <span
            className={`custom-cell ${
              isDisabled
                ? 'svg-path-fill-customGrey-200'
                : 'svg-path-fill-primary-hover svg-path-fill-customGrey-600'
            }`}
          >
            <SvgReview />
          </span>
        </Tooltip>
      )
    default:
      return null
  }
}

export const getAction = <T,>(
  action: TableActionType<T>,
  row: T,
  rowIndex: number,
  onActionClick: OnActionClickCallbackType<T>,
  isDisabled: boolean,
  isHovered?: boolean | undefined
) => {
  const isRowDisabled = isDisabled ? !action?.isEnabledOnDisabledRow : false
  const isActionDisabled =
    typeof action.disabled === 'function'
      ? action.disabled(row)
      : action.disabled ?? false
  const isFinalDisabled = isRowDisabled || isActionDisabled
  switch (action?.type) {
    case TABLE_ACTION_TYPE.TOGGLE:
      return (
        <span>
          <ToggleButton
            data-qa-id={`${action?.type}_${rowIndex}`}
            name={action?.type}
            checked={action?.accessor ? !!row[action?.accessor] : false}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              onActionClick(action?.key, row, rowIndex)
            }}
            disabled={isFinalDisabled}
            tabIndex={action?.tabIndex ?? 0}
          />
        </span>
      )
    case TABLE_ACTION_TYPE.INVERTED_TOGGLE:
      return (
        <span>
          <ToggleButton
            data-qa-id={`${action?.type}_${rowIndex}`}
            name={action?.type}
            checked={action?.accessor ? !row[action?.accessor] : true}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onActionClick(action?.key, row, rowIndex)}
            tabIndex={action?.tabIndex ?? 0}
          />
        </span>
      )
    case TABLE_ACTION_TYPE.COMPONENT:
      return action?.renderActionComponent
        ? action?.renderActionComponent(
            row,
            rowIndex,
            isDisabled && !action?.isEnabledOnDisabledRow
              ? noop
              : onActionClick,
            isDisabled
          )
        : null
    default:
      return (
        <span
          data-qa-id={`${action?.key}_${rowIndex}`}
          className={` p-2 block rounded  ${
            isFinalDisabled ? '' : 'hover:bg-secondary-25 cursor-pointer'
          }
          ${
            (row as { isApprovedDisabled?: boolean }).isApprovedDisabled &&
            action.label == 'Approve'
              ? 'svg-path-fill-disabled fill-accent-200 pointer-events-none'
              : ''
          }
          ${
            (row as { isRejectedDisabled?: boolean }).isRejectedDisabled &&
            action.label == 'Reject'
              ? 'svg-path-fill-disabled fill-accent-200 pointer-events-none'
              : ''
          }
          `}
          onClick={(e) => {
            e?.stopPropagation()
            if (isFinalDisabled) {
              return
            } else {
              onActionClick(action?.key, row, rowIndex)
            }
          }}
          tabIndex={action?.tabIndex ?? 0}
          onKeyDown={(e) => {
            if (e?.key === 'Enter') {
              e?.stopPropagation()
              e?.preventDefault()
              if (isFinalDisabled) {
                return
              } else {
                onActionClick(action?.key, row, rowIndex)
              }
            }
          }}
        >
          <Tooltip content={action.label} key={String(isHovered ?? false)}>
            {action.icon || getIcon(action?.key, isFinalDisabled, isHovered)}
          </Tooltip>
        </span>
      )
  }
}

export const ActionsPopUpList = <T,>({
  handleFalse,
  actions,
  row,
  rowIndex,
  onActionClick = noop,
  tableName = '',
  target,
  isOpen,
}: ActionsPopUpListPropsType<T>) => {
  const actionRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [focusedIndex, setFocusedIndex] = useState<number>(0)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  useOutsideClick({
    ref: actionRef,
    handler: handleFalse,
  })

  useEffect(() => {
    if (isOpen && target) {
      const targetRef = target.getBoundingClientRect()
      const popupWidth = 145
      const popupHeight = actions.length * 60

      const spaceBelow = window.innerHeight - targetRef.bottom
      const spaceAbove = targetRef.top
      const spaceRight = window.innerWidth - targetRef.right
      const spaceLeft = targetRef.left

      let top = 0
      let left = 0

      if (spaceBelow >= popupHeight) {
        top = targetRef.bottom + window.scrollY
      } else if (spaceAbove >= popupHeight) {
        top = targetRef.top - popupHeight + window.scrollY
      } else {
        top = Math.max(spaceAbove, spaceBelow) + window.scrollY
      }

      if (spaceRight >= popupWidth) {
        left = targetRef.left + window.scrollX
      } else if (spaceLeft >= popupWidth) {
        left = targetRef.right - popupWidth + window.scrollX
      } else {
        left = Math.max(spaceLeft, spaceRight) + window.scrollX
      }

      setPosition({ top, left })
    }
  }, [isOpen])
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(0)
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement
      setTimeout(() => {
        actionRef.current?.focus()
      }, 0)
    } else {
      previouslyFocusedElementRef.current?.focus?.()
    }
    return () => {
      previouslyFocusedElementRef.current?.focus?.()
    }
  }, [isOpen])
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % actions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev - 1 < 0 ? actions.length - 1 : prev - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const action = actions[focusedIndex]
      if (action) {
        onActionClick(action.key, row, rowIndex)
        handleFalse()
      }
    } else {
      e.preventDefault()
      handleFalse()
    }
  }
  return isOpen
    ? ReactDOM.createPortal(
        <div
          ref={actionRef}
          role="menu"
          tabIndex={-1} // Make focusable
          onKeyDown={handleKeyDown}
          className="bg-accent-0 border custom-shadow z-20 rounded w-[9.125rem] table-action"
          style={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            zIndex: 100,
          }}
        >
          {actions?.map((action, index) => (
            <div
              data-qa-id={`${action?.key}_${index}`}
              data-action-item
              role="menuitem"
              key={`${tableName}_row_${rowIndex}_action_popup_${index}`}
              className={`hover:bg-secondary-25 border-accent-100 cursor-pointer flex items-center py-2 px-3 gap-1 ${
                focusedIndex === index ? 'bg-secondary-50' : ''
              }`}
              onClick={(e) => {
                e?.stopPropagation()
                onActionClick(action?.key, row, rowIndex)
                handleFalse()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onActionClick(action?.key, row, rowIndex)
                  handleFalse()
                }
              }}
            >
              <span className="">
                {action.icon || getIcon(action?.key, false)}
              </span>
              <span className="font-light text-sm">{action?.label}</span>
            </div>
          ))}
        </div>,
        document.body
      )
    : null
}

export const ActionPopUp = <T,>({
  actions,
  row,
  rowIndex,
  onActionClick,
  tableName = '',
  isDisabled = false,
  setRowPosition,
  isHovered,
}: ActionPopUpPropsType<T>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const target = useRef<HTMLElement>(null)
  const toggleOpen = () => {
    if (isDisabled) return
    setIsOpen((prev) => !prev)
    setRowPosition((prev: boolean) => !prev)
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen()
    }
  }
  const handleClose = () => {
    if (isOpen) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) window.addEventListener('scroll', handleClose, true)
    else window.removeEventListener('scroll', handleClose, true)

    return () => {
      window.removeEventListener('scroll', handleClose, true)
    }
  }, [isOpen])
  return (
    <>
      <span
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-disabled={isDisabled}
        ref={target}
        className={`${
          isDisabled
            ? 'svg-path-fill-accent-200'
            : 'svg-path-fill-secondary-hover hover:bg-secondary-25  cursor-pointer'
        }  p-2 px-3  rounded `}
        onClick={(e) => {
          e?.stopPropagation()
          if (isDisabled) {
            return
          } else {
            toggleOpen()
          }
        }}
        data-qa-id={`three_dots_${rowIndex}`}
        tabIndex={actions?.[0]?.tabIndex ?? 0}
        onKeyDown={handleKeyDown}
      >
        {isHovered ? <SvgBlueEllipses /> : <SvgEllipses />}
      </span>

      <ActionsPopUpList
        tableName={tableName}
        handleFalse={toggleOpen}
        actions={actions}
        row={row}
        rowIndex={rowIndex}
        onActionClick={onActionClick}
        isOpen={isOpen}
        target={target.current}
      />
    </>
  )
}

export const TableActions = <T,>({
  actions,
  row,
  rowIndex,
  onActionClick = noop,
  isDisabled,
  tableName = '',
  isHovered,
  setRowPosition,
}: TableActionsPropsTypes<T>) => {
  const [otherActions, setOtherActions] = useState<TableActionType<T>[]>([])
  const [outActions, setOutActions] = useState<TableActionType<T>[]>([])

  useEffect(() => {
    const other: TableActionType<T>[] = []
    const outA: TableActionType<T>[] = []
    actions?.forEach((action) => {
      if (action?.showInPopUp) {
        other.push(action)
      } else {
        outA.push(action)
      }
    })
    setOtherActions([...other])
    setOutActions([...outA])
  }, [actions])

  return (
    actions && (
      <td className="px-4 py-1 z-10 action-col">
        <div className="flex space-x-2  justify-center items-center relative w-[max-content] mx-auto">
          {outActions?.map((action, index) => {
            const isActionDisabled =
              isDisabled ||
              (typeof action.disabled === 'function'
                ? action.disabled(row)
                : action.disabled ?? false)
            if (action?.renderActionComponent) {
              return action?.renderActionComponent(
                row,
                rowIndex,
                onActionClick,
                isActionDisabled
              )
            } else {
              return (
                <React.Fragment
                  key={`${tableName}_row_${rowIndex}_action_${index}`}
                >
                  {action.key === NAMESPACE.VIEW ||
                  (row as { [key: string]: boolean | string }).manage !== false
                    ? getAction(
                        action,
                        row,
                        rowIndex,
                        onActionClick,
                        isDisabled,
                        isHovered
                      )
                    : null}
                </React.Fragment>
              )
            }
          })}

          {!isEmpty(otherActions) &&
          (row as { [key: string]: boolean | string }).manage !== false ? (
            <ActionPopUp
              isDisabled={isDisabled}
              actions={otherActions}
              row={row}
              rowIndex={rowIndex}
              onActionClick={onActionClick}
              tableName={tableName}
              setRowPosition={setRowPosition}
              isHovered={isHovered}
            />
          ) : null}
        </div>
      </td>
    )
  )
}
